// Phase 6 — Track C T-C2. The shared QueryClient + the navigation bridge.
//
// Why a bridge: react-router's `useNavigate()` is only callable inside a
// component that sits under <BrowserRouter>. The QueryClient itself is a
// plain object created at module scope, so it can't call hooks. To still
// drive navigation from the QueryCache / MutationCache global onError
// handlers — which is the cleanest place to centralise the 401 →
// /login flow — we keep a module-level `navigator` ref that a small
// `<NavigatorBridge/>` component populates on mount (it has access to
// `useNavigate()`).
//
// If, for any reason, the bridge isn't mounted yet (e.g. an error fires
// before the first render of a Router-aware component), we fall back to a
// hard `window.location.assign()` so the user still ends up on /login. The
// fallback is documented; it should be unreachable in normal flows.
//
// Auth lifecycle on 401:
//   1. We tell the AuthContext to drop to 'unauthenticated' via the
//      `signOut` thunk that's registered alongside the navigator.
//   2. We navigate to `/login?from=<currentPath>` so the post-login
//      redirect lands the user back where they were.
//   3. We do NOT call `apiClient.auth.logout()` — the cookie is already
//      invalid; another network round-trip would just race with the
//      redirect.

import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { AuthRequiredError } from '@dailydeck/shared/api';

type NavigateFn = (to: string) => void;
type SignOutFn = () => void;

interface AuthBridge {
  navigate: NavigateFn | null;
  signOut: SignOutFn | null;
}

const bridge: AuthBridge = {
  navigate: null,
  signOut: null,
};

/**
 * Wire the navigation + signOut handlers. Called from `<NavigatorBridge/>`
 * once it has access to `useNavigate()` and `useAuth()`. Idempotent — the
 * latest registration wins.
 */
export function registerAuthBridge(args: { navigate: NavigateFn; signOut: SignOutFn }): void {
  bridge.navigate = args.navigate;
  bridge.signOut = args.signOut;
}

/**
 * Test hook — clear the bridge between tests so a stale registration from
 * a previous test does not leak.
 */
export function resetAuthBridge(): void {
  bridge.navigate = null;
  bridge.signOut = null;
}

function navigateToLoginPreservingPath(): void {
  const from =
    typeof window !== 'undefined' && window.location
      ? `${window.location.pathname}${window.location.search}`
      : '/';
  // Don't loop if we're already on /login.
  if (from.startsWith('/login')) {
    return;
  }
  const target = `/login?from=${encodeURIComponent(from)}`;
  if (bridge.navigate !== null) {
    bridge.navigate(target);
    return;
  }
  // Fallback — bridge not yet registered. Hard navigation gets us to the
  // right place; we lose SPA-level state but that is correct on a 401.
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign(target);
  }
}

function handleGlobalError(error: unknown): void {
  if (error instanceof AuthRequiredError) {
    if (bridge.signOut !== null) {
      bridge.signOut();
    }
    navigateToLoginPreservingPath();
  }
  // Non-auth errors are surfaced by the calling hook (route-level error
  // states / Toasts). The global handler intentionally does nothing for
  // them — silent global toasts on every transient failure would violate
  // §13.1 ("calm and confident").
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleGlobalError,
    }),
    mutationCache: new MutationCache({
      onError: handleGlobalError,
    }),
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
